import {
  Reservoir_CrateType,
  Reservoir_DryProtection,
  Reservoir_GreenType,
} from '@wavingroup/aqora-v2-crate/wavin/aqora/v2/system_pb';
import { TFunction } from 'i18next';
import {
  ReservoirType,
  TankShape,
  VolumeCalcStrategy,
} from '~/shared/models/system/ReservoirModel';
import { assertUnreachable } from '~/types/assert-type';

export class ReservoirTranslationsModel {
  static getLabelForType(type: ReservoirType, t: TFunction) {
    switch (type) {
      case 'conventionalRoof':
        return t('reservoirEdit.fields.type.conventionalRoof');
      case 'blueRoof':
        return t('reservoirEdit.fields.type.blueRoof');
      case 'greenRoof':
        return t('reservoirEdit.fields.type.greenRoof');
      case 'tank':
        return t('reservoirEdit.fields.type.tank');
      case 'infiltrationTank':
        return t('reservoirEdit.fields.type.infiltrationTank');
      case 'pond':
        return t('reservoirEdit.fields.type.pond');
      case 'wadi':
        return t('reservoirEdit.fields.type.wadi');
      default:
        return assertUnreachable(type);
    }
  }

  static getTypeLabelForTooltip(type: ReservoirType, t: TFunction) {
    switch (type) {
      case 'conventionalRoof':
        return t('reservoirEdit.fields.typeForTranslation.roof');
      case 'blueRoof':
        return t('reservoirEdit.fields.typeForTranslation.roof');
      case 'greenRoof':
        return t('reservoirEdit.fields.typeForTranslation.roof');
      case 'tank':
        return t('reservoirEdit.fields.typeForTranslation.tank');
      case 'infiltrationTank':
        return t('reservoirEdit.fields.typeForTranslation.tank');
      case 'pond':
        return t('reservoirEdit.fields.typeForTranslation.pond');
      case 'wadi':
        return t('reservoirEdit.fields.typeForTranslation.wadi');
      default:
        return assertUnreachable(type);
    }
  }

  static getLabelForGreenType(greenType: Reservoir_GreenType, t: TFunction) {
    switch (greenType) {
      case Reservoir_GreenType.DROUGHT_PROOF:
        return t('reservoirEdit.fields.greenType.droughtProof');
      case Reservoir_GreenType.SEDUM:
        return t('reservoirEdit.fields.greenType.sedum');
      case Reservoir_GreenType.BIODIVERSE_SEDUM:
        return t('reservoirEdit.fields.greenType.biodiverseSedum');
      case Reservoir_GreenType.SEED_MIX:
        return t('reservoirEdit.fields.greenType.seedMix');
      case Reservoir_GreenType.GRASSES:
        return t('reservoirEdit.fields.greenType.grasses');
      case Reservoir_GreenType.FULL_GARDEN:
        return t('reservoirEdit.fields.greenType.fullGarden');
      case Reservoir_GreenType.UNSPECIFIED:
        throw new Error('Unsupported green type');
      default:
        return assertUnreachable(greenType);
    }
  }

  static getLabelForDryProtection(
    dryProtection: Reservoir_DryProtection,
    t: TFunction,
  ) {
    switch (dryProtection) {
      case Reservoir_DryProtection.DISABLED:
        return t('reservoirEdit.fields.dryProtection.disabled');
      case Reservoir_DryProtection.GROWING:
        return t('reservoirEdit.fields.dryProtection.growing');
      case Reservoir_DryProtection.OUTSIDE_GROWING:
        return t('reservoirEdit.fields.dryProtection.outsideGrowing');
      case Reservoir_DryProtection.ALWAYS:
        return t('reservoirEdit.fields.dryProtection.always');
      case Reservoir_DryProtection.UNSPECIFIED:
        throw new Error('Unsupported dry protection');
      default:
        return assertUnreachable(dryProtection);
    }
  }

  static getLabelForCrateType(crateType: Reservoir_CrateType, t: TFunction) {
    switch (crateType) {
      case Reservoir_CrateType.WAVIN_AQUACELL_UP_160:
        return t('reservoirEdit.fields.crateType.wavinAquacellUp160');
      case Reservoir_CrateType.WAVIN_AQUACELL_UP_160_CAPILLARITY:
        return t(
          'reservoirEdit.fields.crateType.wavinAquacellUp160Capillarity',
        );
      case Reservoir_CrateType.WAVIN_AQUACELL_UP_85:
        return t('reservoirEdit.fields.crateType.wavinAquacellUp85');
      case Reservoir_CrateType.WAVIN_AQUACELL_UP_85_CAPILLARITY:
        return t('reservoirEdit.fields.crateType.wavinAquacellUp85Capillarity');
      case Reservoir_CrateType.OTHER:
        return t('reservoirEdit.fields.crateType.other');
      case Reservoir_CrateType.UNSPECIFIED:
        return t('reservoirEdit.fields.crateType.unspecified');
      default:
        return assertUnreachable(crateType);
    }
  }

  static getLabelForExternalIrrigation(
    externalIrrigation: boolean,
    t: TFunction,
  ) {
    return externalIrrigation
      ? t('reservoirEdit.fields.externalIrrigation.true')
      : t('reservoirEdit.fields.externalIrrigation.false');
  }

  static getLabelForShape(shape: TankShape, t: TFunction) {
    switch (shape) {
      case 'roundPipe':
        return t('reservoirEdit.fields.shape.roundPipe');
      default:
        return assertUnreachable(shape);
    }
  }

  static getLabelForVolumeCalcStrategy(
    volumeCalcStrategy: VolumeCalcStrategy,
    t: TFunction,
  ) {
    switch (volumeCalcStrategy) {
      case 'WATER_VOLUME':
        return t('reservoirEdit.fields.volumeCalcStrategy.waterVolume');
      case 'WATER_HEIGHT':
        return t('reservoirEdit.fields.volumeCalcStrategy.waterHeight');
      case 'WATER_AREA':
        return t('reservoirEdit.fields.volumeCalcStrategy.waterArea');
      default:
        return assertUnreachable(volumeCalcStrategy);
    }
  }
}
